import { HashRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { useEffect, useState } from 'react'
import { supabase } from './lib/supabase'

// Layouts
import AuthLayout from './layouts/AuthLayout'
import DashboardLayout from './layouts/DashboardLayout'

// Auth Pages
import Login from './pages/auth/Login'
import Signup from './pages/auth/Signup'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard'
import Projects from './pages/dashboard/Projects'
import ProjectDetail from './pages/dashboard/ProjectDetail'
import VersionDetail from './pages/dashboard/VersionDetail'
import Team from './pages/dashboard/Team'
import Invitations from './pages/dashboard/Invitations'
import Subscriptions from './pages/dashboard/Subscriptions'
import Usage from './pages/dashboard/Usage'
import Settings from './pages/dashboard/Settings'
import Security from './pages/dashboard/Security'
import Help from './pages/dashboard/Help'
import Notifications from './pages/dashboard/Notifications'

// Public Pages
import ClientReview from './pages/public/ClientReview'
import Landing from './pages/public/Landing'

// Protected Route Component
const ProtectedRoute = ({ children, requireSuperAdmin = false }) => {
  const { user, loading } = useAuthStore()
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [checkingAdmin, setCheckingAdmin] = useState(requireSuperAdmin)
  
  useEffect(() => {
    if (requireSuperAdmin && user) {
      checkSuperAdmin()
    } else if (!requireSuperAdmin) {
      setCheckingAdmin(false)
    }
  }, [user, requireSuperAdmin])
  
  const checkSuperAdmin = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('is_super_admin')
        .eq('id', user.id)
        .single()
      
      if (!error && data) {
        setIsSuperAdmin(data.is_super_admin || false)
      }
    } catch (err) {
      console.error('Error checking super admin status:', err)
      setIsSuperAdmin(false)
    } finally {
      setCheckingAdmin(false)
    }
  }
  
  if (loading || checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    )
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

function App() {
  const { initialize } = useAuthStore()

  useEffect(() => {
    initialize()
  }, [initialize])

  return (
    <HashRouter>
      <Routes>
        {/* Public Routes - More specific routes first */}
        <Route path="/review/:token" element={<ClientReview />} />
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Protected Dashboard Routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/projects/:projectId/versions/:versionId" element={<VersionDetail />} />
          <Route path="/team" element={<Team />} />
          <Route path="/invitations" element={<Invitations />} />
          <Route path="/subscriptions" element={<Subscriptions />} />
          <Route path="/usage" element={<Usage />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/security" element={<Security />} />
          <Route path="/help" element={<Help />} />
          <Route path="/notifications" element={<Notifications />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashRouter>
  )
}

export default App
